<template>
  <div class="share" v-click-outside="closeMenu">
    <button
      type="button"
      class="share__btn"
      :class="btnClass"
      :aria-label="'Partager cette blague'"
      aria-haspopup="true"
      :aria-expanded="open"
      @click.stop="onClick"
    >
      <span class="share__icon" aria-hidden="true">🔗</span>
      <span v-if="withLabel" class="share__label">Partager</span>
    </button>

    <!-- Desktop / no-native-share popover -->
    <transition name="share-pop">
      <div v-if="open" class="share__menu" @click.stop>
        <a
          class="share__net share__net--x"
          :href="netUrl('x')"
          target="_blank"
          rel="noopener noreferrer"
          @click="closeMenu"
        >𝕏 <span>Twitter / X</span></a>
        <a
          class="share__net share__net--fb"
          :href="netUrl('fb')"
          target="_blank"
          rel="noopener noreferrer"
          @click="closeMenu"
        >f <span>Facebook</span></a>
        <a
          class="share__net share__net--in"
          :href="netUrl('in')"
          target="_blank"
          rel="noopener noreferrer"
          @click="closeMenu"
        >in <span>LinkedIn</span></a>
        <a
          class="share__net share__net--wa"
          :href="netUrl('wa')"
          target="_blank"
          rel="noopener noreferrer"
          @click="closeMenu"
        >🟢 <span>WhatsApp</span></a>
        <button type="button" class="share__net share__net--copy" @click="copyLink">
          🔗 <span>{{ copied ? 'Lien copié !' : 'Copier le lien' }}</span>
        </button>
      </div>
    </transition>

    <span v-if="copied && !open" class="share__toast" role="status">Lien copié&nbsp;!</span>
  </div>
</template>

<script>
// Reusable share control. On mobile (Web Share API available) the button
// triggers the native share sheet directly. Otherwise it opens a small popover
// with per-network share intents (each network builds its own rich preview from
// the Open Graph tags served at /og/item/<id>/). Instagram is intentionally
// absent: it has no web share-intent URL.
export default {
  name: 'ShareButton',
  props: {
    jokeId: { type: [Number, String], required: true },
    // 'card' = compact icon button (in a joke card header),
    // 'bar'  = labelled button (on the joke page toolbar).
    variant: { type: String, default: 'card' },
  },
  data() {
    return { open: false, copied: false, copyTimer: null }
  },
  computed: {
    withLabel() {
      return this.variant === 'bar'
    },
    btnClass() {
      return this.variant === 'bar' ? 'share__btn--bar' : 'share__btn--card'
    },
    shareUrl() {
      const uri = process.env.VUE_APP_GRAPHQL_URI || 'http://localhost:8000/graphql/'
      const backend = uri.replace(/\/graphql\/?$/, '')
      return `${backend}/og/item/${this.jokeId}/`
    },
    shareText() {
      return 'Une blague sur La Banane 🍌'
    },
  },
  methods: {
    onClick() {
      // Prefer the native share sheet when available (mobile).
      if (navigator.share) {
        navigator
          .share({ title: 'La Banane', text: this.shareText, url: this.shareUrl })
          .catch(() => { /* user cancelled — no-op */ })
        return
      }
      this.open = !this.open
    },
    closeMenu() {
      this.open = false
    },
    netUrl(net) {
      const u = encodeURIComponent(this.shareUrl)
      const t = encodeURIComponent(this.shareText)
      switch (net) {
        case 'x':
          return `https://twitter.com/intent/tweet?url=${u}&text=${t}`
        case 'fb':
          return `https://www.facebook.com/sharer/sharer.php?u=${u}`
        case 'in':
          return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`
        case 'wa':
          return `https://wa.me/?text=${t}%20${u}`
        default:
          return this.shareUrl
      }
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.shareUrl)
      } catch {
        // Clipboard blocked — leave the link visible in the popover anyway.
      }
      this.copied = true
      if (this.copyTimer) clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => { this.copied = false }, 2000)
      this.closeMenu()
    },
  },
  directives: {
    // Lightweight click-outside so the popover closes when clicking elsewhere.
    clickOutside: {
      mounted(el, binding) {
        el.__co__ = (e) => { if (!el.contains(e.target)) binding.value(e) }
        document.addEventListener('click', el.__co__)
      },
      unmounted(el) {
        document.removeEventListener('click', el.__co__)
      },
    },
  },
  beforeUnmount() {
    if (this.copyTimer) clearTimeout(this.copyTimer)
  },
}
</script>

<style scoped>
.share { position: relative; display: inline-flex; }

/* --- trigger button --- */
.share__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-family: var(--font-body);
  font-weight: 600;
  color: var(--ink-900);
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  transition: transform 0.12s var(--ease-bounce), background 0.2s;
}
.share__btn:hover { background: var(--banana-100); }
.share__btn--card {
  width: 30px; height: 30px;
  justify-content: center;
  border-radius: 50%;
  padding: 0;
}
.share__btn--bar {
  padding: 8px 16px;
  border-radius: var(--r-pill);
  font-size: 0.95rem;
}
.share__icon { font-size: 0.9rem; line-height: 1; }

/* --- popover --- */
.share__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 60;
  min-width: 190px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-pop);
}
.share__net {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: var(--r-sm);
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--ink-900);
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  width: 100%;
}
.share__net > :first-child {
  display: inline-grid;
  place-items: center;
  width: 22px; height: 22px; flex: none;
  font-weight: 800;
  border-radius: 5px;
}
.share__net:hover { background: var(--banana-100); }
.share__net--x > :first-child { background: #000; color: #fff; }
.share__net--fb > :first-child { background: #1877F2; color: #fff; }
.share__net--in > :first-child { background: #0A66C2; color: #fff; font-size: 0.8rem; }

/* --- copy toast (card variant, when popover closed) --- */
.share__toast {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  white-space: nowrap;
  background: var(--ink-900);
  color: var(--banana-400);
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 9px;
  border-radius: var(--r-pill);
  z-index: 60;
}

.share-pop-enter-active, .share-pop-leave-active { transition: opacity 0.15s, transform 0.15s var(--ease-out); }
.share-pop-enter-from, .share-pop-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
